import React, { useContext, useMemo } from 'react';
import { FragmentInstance } from '@moment/shared-code/widgets.tsx';
import { FeedBuilder } from '@/moment/layouts/MainFeed/FeedBuilder';
import {
  TripodContext,
  TripodContextValue,
} from '@moment/context/TripodContext.tsx';
import { EnhancedLightboxItem } from '@moment/net/softbox-types.ts';
import {
  FRAGMENT_WIDGETS_CONFIG,
  FragmentLayoutItem,
} from '@moment/shared-code/fragment-widgets.tsx';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';

interface LiveFragmentRendererProps {
  pageData?: any; // The page data containing fragment definitions
  fragment: FragmentInstance;
  newsItems: EnhancedLightboxItem[];
  interests: any;
  isLoading: boolean;
  source?: string;
  sourceSchedule?: string;
}

export const LiveFragmentRenderer: React.FC<LiveFragmentRendererProps> = ({
  pageData: pageDataProp,
  fragment,
  newsItems,
  interests,
  isLoading,
  source: fragmentFeedSource,
  sourceSchedule,
}) => {
  const contentTypes = [
    'StorySmall',
    'StoryMedium',
    'StoryLarge',
    'StoryTop',
    'StoryCarousel',
    'GameMedium',
    'GamesTile',
  ];

  const { t } = useTranslation();

  // The context contains pre-fetched data, including the layouts for all fragments on the page.
  const { tripodData } = useContext(TripodContext) as TripodContextValue;
  const { partner } = useContext(CommonPageDataContext);
  const params = useParams();

  // Data resolution is now simpler. It's either passed via props (TabContainer)
  // or comes from the context (standalone pages, which now includes client-fetched data).
  const pageData = pageDataProp || tripodData?.pageData;
  const { fragments = {} } = pageData || {};

  // schedule should first be the path param ('/:schedule') if it exists,
  // else fall back to fragment config
  const schedule = params.schedule ? params.schedule : fragment.props.schedule;
  const publisher = fragment.props.publisher;
  const source = fragment.props.source;

  // The fragment's layout definition is read directly from the context.
  const fragmentData = fragments[fragment.fragmentId];

  // Get the fragment layout items
  const fragmentLayoutItems = useMemo((): FragmentLayoutItem[] | null => {
    if (!fragmentData) return null;

    // Find the active revision, fallback to the latest one
    const activeRevision =
      fragmentData.revisions.find(
        (r: any) => r.id === fragmentData.activeRevisionId
      ) || fragmentData.revisions[0];

    if (
      !activeRevision ||
      !activeRevision.layoutContent ||
      activeRevision.layoutContent.length === 0
    ) {
      return null; // Don't render if fragment has no layout defined
    }

    return activeRevision.layoutContent as FragmentLayoutItem[];
  }, [fragmentData]);

  // Render all items in their original order
  const renderedItems = useMemo(() => {
    if (!fragmentLayoutItems) return null;
    if (isLoading) return null; // Will be handled by the loading check below

    let newsItemIndex = 0;

    return fragmentLayoutItems.map((item, index) => {
      // Check if this is a content component (consumes news items)
      const isContentComponent = contentTypes.includes(item.type);

      if (isContentComponent) {
        // For content components, we need to render them individually
        // We'll create a mini FeedBuilder for each content component
        // Get the next news item for this content component
        const currentNewsItem = newsItems[newsItemIndex];
        newsItemIndex++;

        // Create a single-item layout configuration for this content component
        const singleItemConfig = {
          path: '',
          title: '',
          description: '',
          schedule: schedule,
          publisher: publisher,
          source: source,
          layoutConfiguration: [
            {
              type: 'item',
              component: item.type,
            },
          ],
        };

        return (
          <div key={`content-${index}`} className="fragment-content-container">
            <FeedBuilder
              newsItems={currentNewsItem ? [currentNewsItem] : []}
              interests={interests}
              configuration={singleItemConfig}
              partner={partner}
              source={fragmentFeedSource}
              sourceSchedule={sourceSchedule}
            />
          </div>
        );
      } else {
        // This is a widget (like AdPlacement)
        const widgetConfig = FRAGMENT_WIDGETS_CONFIG.find(
          (w) => w.id === item.type
        );
        if (!widgetConfig) {
          console.warn(
            `LiveFragmentRenderer: Unknown widget type "${item.type}" found in fragment.`
          );
          return (
            <div key={item.instanceId}>Unknown widget type: {item.type}</div>
          );
        }

        const Component = widgetConfig.component;
        return (
          <div
            key={item.instanceId}
            className={`fragment-widget-container fragment-widget-type-${item.type.toLowerCase()}`}
          >
            <Component {...(item.props || {})} />
          </div>
        );
      }
    });
  }, [fragmentLayoutItems, isLoading, newsItems, interests, schedule]);

  if (isLoading) {
    return <div>{t('Loading your content...')}</div>;
  }
  if (!fragmentLayoutItems) {
    return <div>{t('Error loading fragment content or layout.')}</div>;
  }

  return (
    <div
      className="fragment-container"
      style={{ padding: 0, border: 0, margin: 0 }}
    >
      {renderedItems}
    </div>
  );
};
